import { Center } from "@/components/Center/Center";
import { Button } from "@/components/ui/button";
import { useHandleError } from "@/hooks/useHandleError";
import { LoadingSpinner } from "@roman/shared/components/LoadingSpinner";
import { useRecentPlayerStats } from "@roman/shared/hooks/soccer/useRecentPlayerStats";
import { useSoccerRound } from "@roman/shared/hooks/soccer/useSoccerRound";
import type { Player } from "@roman/shared/types/Player";
import { differenceInYears, format } from "date-fns";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AthleteInfo } from "./athleteInfo";
import { defaultGameScore } from "./const";
import { Games } from "./games";
import { Season } from "./season";

const formatFantasyPoints = (points: number): string => {
  const rounded = Math.round(points * 10) / 10;
  return rounded === 0 ? "0" : rounded.toString();
};

export const PlayerDetail = () => {
  const navigate = useNavigate();
  const { playerId } = useParams();
  const roundId = useLocation().state?.roundId;
  const handleError = useHandleError("プレイヤーの詳細取得に失敗しました");
  if (!playerId || !roundId) {
    throw new Error("playerDetail error");
  }

  const roundDetailHandleError = useHandleError("ラウンドの詳細取得に失敗しました");
  const { data: roundData } = useSoccerRound(roundId, roundDetailHandleError, "ja");

  const [player, setPlayer] = useState<Player>();
  // This hook only gets data from the last 8 games.
  const { data } = useRecentPlayerStats(playerId, roundId, handleError, "ja");

  if (!player) {
    throw new Error("playerDetail error");
  }

  useEffect(() => {
    if (!roundData) {
      return;
    }

    const players = [...(roundData.round?.players.awayPlayers || []), ...(roundData.round?.players.homePlayers || [])];
    const selectedPlayer = players.find((item) => {
      return item.id === playerId;
    });

    setPlayer(selectedPlayer);
  }, [playerId, roundData]);

  const handleGoBack = () => {
    navigate(-1);
  };

  if (!data) {
    return (
      <Center>
        <LoadingSpinner />
      </Center>
    );
  }

  const athelete = [
    { item: "国籍", info: data.nationality },
    {
      item: "生年月日",
      info: data.dateOfBirth
        ? `${format(data.dateOfBirth, "yyyy年M月d日")} (${differenceInYears(Date.now(), data.dateOfBirth)}歳)`
        : "-",
    },
    { item: "身長", info: data.height ? `${data.height}cm` : "-" },
    { item: "体重", info: data.weight ? `${data.weight}kg` : "-" },
  ];

  const seasonRecord = [
    { item: "ゴール", info: "-" },
    { item: "PKゴール", info: "-" },
    { item: "アシスト", info: "-" },
    { item: "シュート", info: "-" },
    { item: "総出場時間", info: "-" },
    {
      item: "FP",
      info: player ? (Math.round(player.averageFantasyPoint * 10) / 10).toString() : "-",
      description: {
        heading: "FP: Fantasy Point (活躍ポイント) とは?",
        content: "選手の試合でのパフォーマンスに基づいて計算される活躍ポイントです。",
      },
    },
  ];

  const gameScores = !data.recentStats
    ? [defaultGameScore]
    : data.recentStats.map((stat) => {
        return {
          record: [
            { name: "試合日", info: format(stat.startDate, "yyyy/MM/dd") },
            { name: "対戦相手", info: stat.opponent },
            { name: "試合スコア", info: stat.matchScore },
            { name: "リーグ名", info: data.seasonName },
            {
              name: "FP",
              info: stat.played ? formatFantasyPoints(stat.fantasyPoint) : "0",
            },
            {
              name: "ゴール",
              info: stat.played ? stat.gameStat.goalsScored : "-",
            },
            {
              name: "アシスト",
              info: stat.played ? stat.gameStat.assists : "-",
            },
            {
              name: "シュート",
              info: stat.played ? stat.gameStat.shotsOnTarget : "-",
            },
            {
              name: "枠内シュート",
              info: stat.played ? stat.gameStat.shotsOnTarget : "-",
            },
            {
              name: "クロス",
              info: stat.played ? stat.gameStat.crossesSuccessful : "-",
            },
            {
              name: "決定機パス",
              info: stat.played ? stat.gameStat.chancesCreated : "-",
            },
            {
              name: "正確なパス",
              info: stat.played ? stat.gameStat.passesSuccessful : "-",
            },
            {
              name: "被ファール",
              info: stat.played ? stat.gameStat.foulsCommitted : "-",
            },
            {
              name: "ファール",
              info: stat.played ? stat.gameStat.foulsCommitted : "-",
            },
            {
              name: "タックル",
              info: stat.played ? stat.gameStat.tacklesSuccessful : "-",
            },
            {
              name: "イエローカード",
              info: stat.played ? stat.gameStat.yellowCards : "-",
            },
            {
              name: "レッドカード",
              info: stat.played ? stat.gameStat.redCards : "-",
            },
            {
              name: "インターセプト",
              info: stat.played ? stat.gameStat.interceptions : "-",
            },
            {
              name: "クリーンシート",
              info: stat.played ? stat.gameStat.cleanSheets : "-",
            },
            {
              name: "セーブ",
              info: stat.played ? stat.gameStat.shotsFacedSaved : "-",
            },
            {
              name: "失点",
              info: stat.played ? stat.gameStat.goalsConceded : "-",
            },
            { name: "勝利", info: stat.played ? stat.gameStat.wonGame : "-" },
            {
              name: "PKセーブ",
              info: stat.played ? stat.gameStat.penaltiesSaved : "-",
            },
          ],
        };
      });

  return (
    <div className="bg-white min-w-full relative min-h-screen">
      <div className="z-50 fixed bottom-4 right-4 xl:right-[375px]">
        <Button
          className="p-1 w-[50px] h-[50px] bg-[#176BF2] cursor-pointer rounded-full text-white hover:bg-blue-300 flex flex-col items-center gap-0 justify-center text-[6.67px] font-semibold"
          onClick={handleGoBack}
        >
          <img
            src="/assets/details-close.svg"
            alt="close button"
            className="h-5 w-5 fill-white text-white inline-block p-0 m-0"
          />
          閉じる
        </Button>
      </div>
      <div className="pt-[23px] flex flex-col items-center gap-[23px]">
        {/* Athelete info */}
        <div className="px-[23px] flex items-center justify-between w-full gap-4 h-full">
          <img
            src={`${player.imageUrl || "/assets/player-1.png"}`}
            alt="uniform"
            className="w-[146.67px] h-[220px] object-cover rounded-md"
            width={146.67}
            height={220}
          />
          <div className="flex flex-col items-start justify-between w-full gap-2.5 h-[220px] ">
            <div className="w-full relative">
              <div className="flex flex-col items-start gap-1.5">
                <div className="flex flex-col items-start gap-1">
                  <h3 className="text-lg font-bold text-black">{player.name || ""}</h3>
                  <p className="pl-1 text-xs font-normal text-black tracking-widest">{player.name || ""}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start w-full">
              {athelete.map((content, index) => {
                return (
                  <AthleteInfo
                    key={`athlete-${content.item}`}
                    item={content.item}
                    info={content.info}
                    isLastItem={index === athelete.length - 1}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* season scores */}
        <Season heading="シーズン成績" record={seasonRecord} />

        {/* games scores */}
        <Games heading="試合ごとの成績" scores={gameScores} />
      </div>
    </div>
  );
};
